// Cardiograph + motion tokens for HardRestart UX (TASK-2494).
// All colours come from the Amazonas Inovare design system
// (docs/DESIGN_SYSTEM.md sections 2 + 4.2). No hex hardcoded outside
// this file — components consume via these tokens.

import type { CardiographState } from './types'

// ── Brand palette (mirrors --ai-brand-* tokens) ─────────────────────────

export const COLOR = {
  brandAzul: '#025468',
  brandVerde: '#098d93',
  brandGradient: 'linear-gradient(135deg, #025468 0%, #098d93 100%)',
  warning: '#f59f00',
  danger: '#e03131',
  muted: '#94aab3',
  surfaceDark: '#0d1117', // terminal-style raw output background
  shadow: 'rgba(2, 84, 104, 0.10)',
  glow: 'rgba(9, 141, 147, 0.35)',
} as const

// ── Color-scheme-aware surface tokens (light + dark via light-dark()) ───
//
// Uses CSS `light-dark()` function so the value flips automatically based
// on `color-scheme` set by Mantine. Dark values mirror DESIGN_SYSTEM.md
// section 10 (cards #122f3a, borders subtle white alpha).

export const SURFACE = {
  card: 'light-dark(#ffffff, #122f3a)',
  cardBorder: 'light-dark(#e6f3f5, rgba(255, 255, 255, 0.08))',
  cardShadow: 'light-dark(0 1px 2px rgba(2, 84, 104, 0.10), 0 1px 2px rgba(0, 0, 0, 0.30))',
  textPrimary: 'light-dark(#1a2e35, #e6f3f5)',
  textSecondary: 'light-dark(#5f7882, #94aab3)',
} as const

// ── Cardiograph state colours ───────────────────────────────────────────
//
// Each state paints the trace in a single colour. Amplitude + rhythm
// are owned by `cardiographMotion.ts`. Glow filter intensity below.

export const CARDIO_COLOR: Record<CardiographState, string> = {
  idle: COLOR.muted,
  inspecting: COLOR.brandVerde,
  stopping: COLOR.warning,
  stopped: COLOR.muted,
  cleaning_volumes: COLOR.danger,
  pulling: COLOR.brandAzul,
  recreating: COLOR.brandAzul,
  awaiting_healthy: COLOR.brandVerde,
  healthy: COLOR.brandVerde,
  failed: COLOR.danger,
}

// Glow drop-shadow blur radius per state. 0 = no glow.
export const CARDIO_GLOW: Record<CardiographState, number> = {
  idle: 0,
  inspecting: 4,
  stopping: 4,
  stopped: 0,
  cleaning_volumes: 8,
  pulling: 6,
  recreating: 6,
  awaiting_healthy: 8,
  healthy: 12,
  failed: 0, // freeze: no glow when dead
}

// ── Motion timings ──────────────────────────────────────────────────────

export const MOTION = {
  // View-state transitions (preflight → live → result/error)
  viewSwap: { duration: 0.35, ease: [0.16, 1, 0.3, 1] }, // ease-out-expo
  viewSwapReducedMotion: { duration: 0.1, ease: 'linear' as const },

  // Phase node entrance (Timeline)
  phaseStagger: 0.08,
  phaseEntrance: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },

  // Cardiograph trace tick interval (ms)
  cardioTick: 60,
  cardioPoints: 120, // points in the rolling buffer

  // Volume shred countdown (s)
  shredCountdown: 3,

  // Generic spring used for cardiograph colour/amplitude ramps
  rampSpring: { type: 'spring' as const, stiffness: 120, damping: 22 },
} as const

// ── Polling cadence (ms) — phase-aware ──────────────────────────────────

export const POLL_INTERVAL_MS = {
  default: 1500,
  pulling: 1000, // more output to render
  stopping: 500, // SIGKILL countdown territory
  awaiting_healthy: 1000,
} as const
